export interface NaluEntry {
  start: number;
  dataStart: number;
  type: number;
}

export interface AvcDecoderConfig {
  codec: string;
  description: Uint8Array;
}

export interface H264ParameterSets {
  sps: Uint8Array | null;
  pps: Uint8Array | null;
}

export function findNalus(data: Uint8Array): NaluEntry[] {
  const result: NaluEntry[] = [];
  let i = 0;

  while (i + 3 < data.length) {
    if (data[i] === 0 && data[i + 1] === 0) {
      if (data[i + 2] === 1) {
        if (i + 3 < data.length) {
          result.push({ start: i, dataStart: i + 3, type: data[i + 3] & 0x1f });
        }
        i += 3;
        continue;
      }
      if (i + 3 < data.length && data[i + 2] === 0 && data[i + 3] === 1) {
        if (i + 4 < data.length) {
          result.push({ start: i, dataStart: i + 4, type: data[i + 4] & 0x1f });
        }
        i += 4;
        continue;
      }
    }
    i++;
  }

  return result;
}

export function naluEnd(nalus: NaluEntry[], idx: number, total: number): number {
  return idx + 1 < nalus.length ? nalus[idx + 1].start : total;
}

export function buildAvccDescription(sps: Uint8Array, pps: Uint8Array): Uint8Array {
  if (sps.length < 4) throw new Error("SPS H264 trop court pour construire avcC");
  if (pps.length === 0) throw new Error("PPS H264 vide");

  const out = new Uint8Array(8 + sps.length + 3 + pps.length);
  let o = 0;
  out[o++] = 1; // configurationVersion
  out[o++] = sps[1]; // profile_idc
  out[o++] = sps[2]; // profile_compatibility
  out[o++] = sps[3]; // level_idc
  out[o++] = 0xff; // lengthSizeMinusOne = 3, so each NALU uses a 4-byte length prefix
  out[o++] = 0xe1; // numOfSequenceParameterSets = 1
  out[o++] = (sps.length >> 8) & 0xff;
  out[o++] = sps.length & 0xff;
  out.set(sps, o);
  o += sps.length;
  out[o++] = 1; // numOfPictureParameterSets = 1
  out[o++] = (pps.length >> 8) & 0xff;
  out[o++] = pps.length & 0xff;
  out.set(pps, o);
  o += pps.length;
  return out.slice(0, o);
}

export function extractAvcDecoderConfig(annexBKeyframe: Uint8Array): AvcDecoderConfig | null {
  const sets = extractH264ParameterSets(annexBKeyframe);
  if (!sets.sps || !sets.pps) return null;
  return buildAvcDecoderConfig(sets.sps, sets.pps);
}

export function buildAvcDecoderConfig(sps: Uint8Array, pps: Uint8Array): AvcDecoderConfig | null {
  if (sps.length < 4 || pps.length === 0) return null;

  const profile = sps[1].toString(16).padStart(2, "0");
  const compatibility = sps[2].toString(16).padStart(2, "0");
  const level = sps[3].toString(16).padStart(2, "0");
  return {
    codec: `avc1.${profile}${compatibility}${level}`,
    description: buildAvccDescription(sps, pps),
  };
}

export function extractH264ParameterSets(annexB: Uint8Array): H264ParameterSets {
  const nalus = findNalus(annexB);
  let sps: Uint8Array | null = null;
  let pps: Uint8Array | null = null;

  for (let i = 0; i < nalus.length; i++) {
    const nalu = nalus[i];
    if (nalu.type !== 7 && nalu.type !== 8) continue;
    const data = annexB.slice(nalu.dataStart, naluEnd(nalus, i, annexB.length));
    if (nalu.type === 7 && data.length >= 4) sps = data;
    if (nalu.type === 8 && data.length > 0) pps = data;
  }

  return { sps, pps };
}

// Convert an Annex-B access unit into AVCC sample data for VideoDecoder configs
// that carry an AVCDecoderConfigurationRecord in `description`.
export function annexBToAvcc(annexB: Uint8Array): Uint8Array {
  const nalus = findNalus(annexB);
  if (nalus.length === 0) return annexB;

  const sampleNalus = nalus
    .map((n, idx) => ({ n, idx }))
    // Keep only VCL NALUs (slice data). Strip parameter sets (7=SPS, 8=PPS),
    // access-unit delimiters (9), filler (12), and SEI (6) — VideoToolbox
    // rejects AVCC samples that mix SEI with VCL NALUs.
    .filter(({ n }) => n.type !== 6 && n.type !== 7 && n.type !== 8 && n.type !== 9 && n.type !== 12);

  if (sampleNalus.length === 0) return new Uint8Array();

  let size = 0;
  for (const { n, idx } of sampleNalus) {
    size += 4 + (naluEnd(nalus, idx, annexB.length) - n.dataStart);
  }

  const out = new Uint8Array(size);
  let off = 0;
  for (const { n, idx } of sampleNalus) {
    const end = naluEnd(nalus, idx, annexB.length);
    const len = end - n.dataStart;
    out[off++] = (len >> 24) & 0xff;
    out[off++] = (len >> 16) & 0xff;
    out[off++] = (len >> 8) & 0xff;
    out[off++] = len & 0xff;
    out.set(annexB.subarray(n.dataStart, end), off);
    off += len;
  }

  return out;
}
